/**
 * Rules register — the automation kernel's rule DSL, on the shared
 * `ResourceListPage` grammar (PageHeader + search + DataTable + RowActions).
 * No id column: the rule **name** leads, followed by its lifecycle status,
 * trigger event, effect, and priority.
 */
import { useMemo, useState } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";

import type { Rule } from "@aos/shared";
import { StatusBadge } from "@/components/app/StatusBadge";
import { ResourceListPage, RowActions } from "../_shared/ListScaffold";

/** Rules register (server-side DataTable). */
export function RuleList() {
  const { edit, show } = useNavigation();
  const [pendingDelete, setPendingDelete] = useState<Rule | null>(null);

  const columns = useMemo<ColumnDef<Rule, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
      },
      {
        id: "trigger_event_type",
        accessorKey: "trigger_event_type",
        header: "Trigger",
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {(getValue() as string) || "—"}
          </span>
        ),
      },
      {
        id: "effect_type",
        accessorKey: "effect_type",
        header: "Effect",
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">
            {(getValue() as string) || "—"}
          </span>
        ),
      },
      {
        id: "priority",
        accessorKey: "priority",
        header: "Priority",
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <RowActions
              onShow={() => show("rules", row.original.id)}
              onEdit={() => edit("rules", row.original.id)}
              onDelete={() => setPendingDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [show, edit],
  );

  return (
    <ResourceListPage<Rule>
      resource="rules"
      title="Rules"
      description="The automation kernel's rule DSL — each rule's trigger, guard conditions, and effect."
      createLabel="New Rule"
      columns={columns}
      initialSort={[{ field: "priority", order: "asc" }]}
      searchPlaceholder="Search rules…"
      emptyMessage="No rules yet."
      deleteEntityLabel="rule"
      deleteName={(rule) => rule.name}
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default RuleList;
